import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types/plugin-factory';
import type { GetMentionNameDetails } from '@atlaskit/editor-mention-types/types';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import type { AIPlugin } from '../../aiPluginType';
import type { ErrorAIEvent, StreamAIEvent } from '../../editor-actions/types';
import type { EditorPluginAIProvider } from '../../types/types';
import { configItemUsesRovo } from '../../ui/components/SessionUI/utils/checkMigrationStatus';
import { countCompleteBlocks, takeCompleteBlocks } from '../../ui/utils/complete-blocks';
import { isQuickActionsBlockStreamingEnabled } from '../../ui/utils/quick-actions-block-streaming-enabled';
import { createStreamFlowPacer } from '../../ui/utils/request/stream-flow-pacer';
import { createStreaming } from '../../ui/utils/streaming';
import {
	resolveQuickActionAgentAttribution,
	shouldResolveQuickActionAgentAttribution,
} from '../agent-attribution/resolveQuickActionAgentAttribution';
import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pure-pm-in-editor-direct-streaming/pluginKey';
import { aiSessionContextPluginKey } from './ai-session-context-plugin-key';
import {
	streamADFResponse,
	completeADFStream,
	errorADFStream,
	resetSessionState,
	setAgentAttribution,
	updateIdMap,
	updateTraceIds,
} from './commands';
import {
	AI_ORIGINAL_NODE_DATA,
	type AIOriginalNodeData,
	CAPTURE_ABORTED_TOOL_CALL_POSITIONS,
	type CaptureAbortedToolCallPositionsData,
	CELL_KIND_CONVERSION_RESULT,
	type CellKindConversionResultData,
} from './meta-keys';
import { reducer } from './reducer';
import type { SessionContextPluginState, SessionContextAction } from './types';

export const initialState: SessionContextPluginState = {
	adfResponse: undefined,
	status: 'idle',
	responseVersion: 0,
	streamPaused: false,
	hasUserScrolled: false,
	// Positions of tool calls aborted by the queue timeout, keyed by id. Remapped
	// on every doc change and consumed on resume.
	abortedToolCallPositions: {},
	// Id of a tool call awaiting final-position capture in the next
	// buildCommitTransaction; cleared once captured.
	pendingForceAbortedToolCallId: undefined,
};

export interface AISessionContextPluginOptions {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	dispatch: PMPluginFactoryParams['dispatch'];
	editorPluginAIProvider: EditorPluginAIProvider;
	getIntl: PMPluginFactoryParams['getIntl'];
	getMentionNameDetails: GetMentionNameDetails;
}

/**
 * This plugin stores the core business logic state for the AI session.
 * It manages the session lifecycle including:
 * - State transitions (idle → streaming → complete)
 * - Streaming initiation for non-orchestrator prompts
 * - Bridging stream events back to plugin state
 *
 * The view.update() method detects when streaming should start (based on decoration state)
 * and initiates the network request, bridging responses back to this plugin's state.
 * The pure-pm plugin then reacts to state changes to update the document.
 */
export const createAISessionContextPlugin = (
	options: AISessionContextPluginOptions,
): SafePlugin => {
	const { api, dispatch, editorPluginAIProvider, getIntl, getMentionNameDetails } = options;

	return new SafePlugin<SessionContextPluginState>({
		key: aiSessionContextPluginKey,
		state: {
			init: () => initialState,
			apply: (tr: ReadonlyTransaction, pluginState: SessionContextPluginState) => {
				let nextState: SessionContextPluginState = pluginState;

				// Remap stored aborted-tool-call positions on every doc change so a
				// resumed tool call re-applies where it was originally working,
				// accounting for content other tool calls inserted/removed after it was
				// aborted. Start after preceding inserts (1), end before following
				// inserts (-1).
				if (tr.docChanged && nextState.abortedToolCallPositions) {
					const ids = Object.keys(nextState.abortedToolCallPositions);
					if (ids.length) {
						const remappedAborted: NonNullable<
							SessionContextPluginState['abortedToolCallPositions']
						> = {};
						for (const id of ids) {
							const entry = nextState.abortedToolCallPositions[id];
							remappedAborted[id] = {
								startPos: tr.mapping.map(entry.startPos, 1),
								aiContentEndPos: tr.mapping.map(entry.aiContentEndPos, -1),
							};
						}
						nextState = { ...nextState, abortedToolCallPositions: remappedAborted };
					}
				}

				// Retain the exact command target until the streaming mount consumes it.
				const originalNodeData = tr.getMeta(AI_ORIGINAL_NODE_DATA) as
					| AIOriginalNodeData
					| undefined;
				if (originalNodeData) {
					nextState = {
						...nextState,
						replaceNodeOriginalNode: originalNodeData.originalNode,
					};
				}

				// Persist a force-finished tool call's final positions from the meta
				// stamped by `buildCommitTransaction`. Stored by id (then remapped on
				// every later doc change above); captured exactly once.
				const capturedAbortedPositions = tr.getMeta(CAPTURE_ABORTED_TOOL_CALL_POSITIONS) as
					| CaptureAbortedToolCallPositionsData
					| undefined;
				if (capturedAbortedPositions) {
					nextState = {
						...nextState,
						abortedToolCallPositions: {
							...nextState.abortedToolCallPositions,
							[capturedAbortedPositions.toolCallId]: {
								startPos: capturedAbortedPositions.startPos,
								aiContentEndPos: capturedAbortedPositions.aiContentEndPos,
							},
						},
						pendingForceAbortedToolCallId: undefined,
					};
				}

				const cellKindResult = tr.getMeta(CELL_KIND_CONVERSION_RESULT) as
					| CellKindConversionResultData
					| undefined;
				if (cellKindResult) {
					nextState = {
						...nextState,
						cellKindConversionRejection: cellKindResult.rejection,
					};
				}

				// Process dispatched plugin-key actions.
				const meta = tr.getMeta(aiSessionContextPluginKey);
				if (meta) {
					nextState = reducer({
						state: nextState,
						action: meta as SessionContextAction,
					});
				}

				if (nextState !== pluginState) {
					dispatch(aiSessionContextPluginKey, nextState);
				}
				return nextState;
			},
		},
		/**
		 * When all prompts are migrated to use the orchestrator, this entire view can be removed.
		 */
		view: () => {
			// Replaced on stop: a stopped stream's request keeps running and pushing into
			// its pacer, which would otherwise emit into the next stream's session.
			// One-chunk apply has nothing to pace, so the response is delivered as soon
			// as the request drains.
			let streamFlowPacer = isQuickActionsBlockStreamingEnabled() ? null : createStreamFlowPacer();
			// Owned here so a stop can abort mid-flight — `createStreaming` only returns
			// its abort cleanup once the response has fully drained.
			let abortController: AbortController | undefined = expValEqualsNoExposure(
				'platform_editor_remove_pause_streaming',
				'isEnabled',
				true,
			)
				? new AbortController()
				: undefined;
			let abortStreaming: (() => void) | undefined;
			let hasStartedStreaming = false;
			// Each request needs its own identity: an older request may drain after a new one starts.
			let streamGeneration = 0;
			// Streaming setup is async; if cancel/reset happens before cleanup is available,
			// this "sticky" flag ensures we abort immediately once cleanup resolves.
			// It also guards against late/buffered events reactivating plugin state after cancel/reset.
			let cancelled = false;

			const cancelStreaming = () => {
				streamGeneration += 1;
				cancelled = true;
				hasStartedStreaming = false;
				streamFlowPacer?.destroy();
				streamFlowPacer = isQuickActionsBlockStreamingEnabled() ? null : createStreamFlowPacer();
				if (expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true)) {
					abortController?.abort();
					abortController = new AbortController();
				}
				if (abortStreaming) {
					abortStreaming();
					abortStreaming = undefined;
				}
			};

			return {
				update(view: EditorView, previousState: EditorState) {
					const sessionState = aiSessionContextPluginKey.getState(view.state);
					const prevSessionState = aiSessionContextPluginKey.getState(previousState);
					const decorationState = aiExperienceDecorationPluginKey.getState(view.state);
					const prevDecorationState = aiExperienceDecorationPluginKey.getState(previousState);

					// Handle cancel request - detect when cancelRequested flag was just set
					const cancelJustRequested =
						sessionState?.cancelRequested && !prevSessionState?.cancelRequested;
					if (cancelJustRequested) {
						cancelStreaming();
					}

					// Reset streaming flag and abort any in-flight request when session ends
					// This handles both explicit cancel and implicit reset
					if (sessionState?.status === 'idle') {
						cancelStreaming();
						return;
					}

					// For the fallback free-gen path: reset session state after finalize completes.
					// The 'complete' event handler defers the reset so buildCommitTransaction can
					// still read markdown/idMap from session state during the finalize flow.
					// Once finalize is done (hasFinalStream: true), it's safe to reset.
					if (sessionState?.status === 'complete' && sessionState?.userInput !== undefined) {
						const streamingState = purePmInEditorDirectStreamingPluginKey.getState(view.state);
						if (streamingState?.hasFinalStream) {
							api?.core?.actions.execute(resetSessionState());
							return;
						}
					}

					// When streamPaused changes, pause or resume the pacer so the discard screen can stop delivery
					if (sessionState?.streamPaused !== prevSessionState?.streamPaused) {
						if (sessionState?.streamPaused) {
							streamFlowPacer?.pause();
						} else {
							streamFlowPacer?.resume();
						}
					}

					// Detect when we should start streaming:
					// - configItem just became available (decoration state changed)
					// - a new Quick Action interaction opened while its previous config item remains set
					// - Not a Rovo/orchestrator prompt
					// - Haven't already started streaming for this session
					const configItemJustSet = decorationState?.configItem && !prevDecorationState?.configItem;
					const directStreamJustOpened =
						sessionState?.status === 'streaming' &&
						!sessionState.sessionId &&
						decorationState?.interactionSessionId !== prevDecorationState?.interactionSessionId &&
						decorationState?.configItem &&
						!configItemUsesRovo(decorationState.configItem.key);
					if (directStreamJustOpened) {
						cancelStreaming();
					}
					const shouldStartStream =
						(configItemJustSet || directStreamJustOpened) &&
						decorationState?.positions &&
						!hasStartedStreaming &&
						!configItemUsesRovo(decorationState.configItem?.key);

					// Detect when the fallback free-gen prompt submits user input.
					// This happens when START_FREE_GEN_STREAM action is dispatched from the PromptScreen
					// for products that don't have Rovo/onSubmit (e.g. Jira, Bitbucket).
					const freeGenStreamJustStarted =
						sessionState?.status === 'streaming' &&
						prevSessionState?.status === 'prompt' &&
						sessionState?.userInput !== undefined &&
						!hasStartedStreaming &&
						decorationState?.configItem &&
						decorationState?.positions;

					if (!shouldStartStream && !freeGenStreamJustStarted) {
						return;
					}

					if (!decorationState?.configItem || !decorationState?.positions) {
						return;
					}
					const { configItem, positions } = decorationState;

					hasStartedStreaming = true;
					cancelled = false;
					const currentStreamGeneration = streamGeneration;

					const startStreaming = () => {
						// Create the streaming handler
						// Note: Analytics for streaming is handled separately via the AnalyticsFlow context
						// in the React layer. This needs fixing
						const streamingHandler = createStreaming({
							abortController,
							fireAIAnalyticsEvent: () => {},
							streamFlowPacer,
						});

						// Build the request context
						const isEmpty = positions[0] === positions[1];
						const selectionType = isEmpty ? 'empty' : 'range';
						const context = {
							aiProvider: editorPluginAIProvider,
							intl: getIntl(),
							editorView: view,
							positions,
							isEmpty,
							selectionType: selectionType as 'empty' | 'range',
							configItem,
							userInput: freeGenStreamJustStarted ? (sessionState?.userInput ?? '') : '',
							analyticsContext: { aiSessionId: '', aiGroupId: '' },
							usePageKnowledge: true,
							getMentionNameDetails,
							responseHistory: { entries: [], positionFromEnd: 0, totalSize: 0, traversals: 0 },
						};

						// Block streaming publishes whole blocks only, so the document snaps forward
						// a block at a time instead of character by character. The latest chunk is
						// retained as a fallback for terminal events that arrive without content.
						const blockStreaming = isQuickActionsBlockStreamingEnabled();
						let resolved: Pick<StreamAIEvent, 'adfResponse' | 'markdown'> = {};
						let publishedBlocks = 0;

						// Invoke streaming and bridge events to session context
						streamingHandler(context, (event) => {
							if (cancelled || currentStreamGeneration !== streamGeneration) {
								return;
							}
							switch (event.type) {
								case 'stream':
									if (event.adfResponse || event.markdown) {
										if (blockStreaming) {
											resolved = {
												adfResponse: event.adfResponse,
												markdown: event.markdown,
											};

											// Publish only whole blocks, so the document snaps forward a
											// block at a time rather than character by character. The
											// trailing block is withheld until the stream terminates.
											const completeBlocks = countCompleteBlocks(resolved);
											if (completeBlocks > publishedBlocks) {
												publishedBlocks = completeBlocks;
												api?.core?.actions.execute(
													streamADFResponse({
														type: 'stream',
														...takeCompleteBlocks(resolved, completeBlocks),
													}),
												);
											}
											break;
										}
										api?.core?.actions.execute(
											streamADFResponse({
												type: 'stream',
												adfResponse: event.adfResponse,
												markdown: event.markdown,
											}),
										);
									}
									break;
								case 'complete':
									resolved = blockStreaming
										? {
												adfResponse: event.adfResponse ?? resolved.adfResponse,
												markdown: event.markdown ?? resolved.markdown,
											}
										: { adfResponse: event.adfResponse, markdown: event.markdown };

									// The finaliser only commits after the session has passed through
									// `streaming`. A single-block response never reaches a block
									// boundary mid-stream, so the response is published as one stream
									// immediately before completing.
									if (
										blockStreaming &&
										publishedBlocks === 0 &&
										(resolved.adfResponse || resolved.markdown)
									) {
										api?.core?.actions.execute(
											streamADFResponse({
												type: 'stream',
												adfResponse: resolved.adfResponse,
												markdown: resolved.markdown,
											}),
										);
									}

									if (resolved.adfResponse || resolved.markdown) {
										api?.core?.actions.execute(
											completeADFStream({
												type: 'complete',
												adfResponse: resolved.adfResponse,
												markdown: resolved.markdown,
											}),
										);
									}
									// Free-gen path keeps session state alive for the UI layer to reset
									// after accept/reject. Quick-action path resets immediately.
									if (!freeGenStreamJustStarted) {
										api?.core?.actions.execute(resetSessionState());
									}
									break;
								case 'response too similar':
									api?.core?.actions.execute(
										completeADFStream({
											type: 'complete',
											responseTooSimilar: true,
										}),
									);

									break;
								case 'error':
									if (event.errorInfo) {
										api?.core?.actions.execute(errorADFStream(event as ErrorAIEvent));
									}
									break;
								case 'update traceIds':
									if (event.traceIds) {
										api?.core?.actions.execute(updateTraceIds(event.traceIds));
									}
									break;
								case 'update idMap':
									if (event.context?.idMap) {
										api?.core?.actions.execute(updateIdMap(event.context.idMap));
									}
									break;
								default:
									break;
							}
						}).then((cleanup) => {
							if (!cleanup) {
								return;
							}
							// If cancel/reset happened before cleanup was available, abort immediately.
							if (cancelled || currentStreamGeneration !== streamGeneration) {
								cleanup();
								return;
							}
							abortStreaming = cleanup;
						});
					};

					if (shouldResolveQuickActionAgentAttribution(configItem.key)) {
						void (async () => {
							const agentAttribution = await resolveQuickActionAgentAttribution({
								editorPluginAIProvider,
							});
							if (cancelled || currentStreamGeneration !== streamGeneration || view.isDestroyed) {
								return;
							}
							api?.core?.actions.execute(setAgentAttribution(agentAttribution));
							if (cancelled || currentStreamGeneration !== streamGeneration || view.isDestroyed) {
								return;
							}
							startStreaming();
						})();
						return;
					}

					startStreaming();
				},

				destroy() {
					abortStreaming?.();
					streamFlowPacer?.destroy();
				},
			};
		},
	});
};

/**
 * Gets the plugin state from an editor state.
 */
export const getAIExperienceContextPluginState = (
	state: EditorState,
): SessionContextPluginState | undefined => {
	return aiSessionContextPluginKey.getState(state);
};

export const createCommand = (action: SessionContextAction): EditorCommand => {
	return ({ tr }: { tr: Transaction }) => {
		return tr.setMeta(aiSessionContextPluginKey, action);
	};
};
